"use client"

import { useState } from "react"
import type { AttackType, DefenseType } from "@/types/hero.types"
import { DamageTypeTooltip } from "@/components/ui/damage-type-tooltip"
import { ArmorTypeTooltip } from "@/components/ui/armor-type-tooltip"

interface MobileStatsPanelProps {
  isOpen: boolean
  onClose: () => void
  unitName: string
  damageType: AttackType
  armorType: DefenseType
  armorValue: number
  isDesktop?: boolean
  pillarWidth?: number
}

type TabType = "damage" | "armor"

export const MobileStatsPanel = ({
  isOpen,
  onClose,
  unitName,
  damageType,
  armorType,
  armorValue,
}: MobileStatsPanelProps) => {
  const [activeTab, setActiveTab] = useState<TabType>("damage")

  if (!isOpen) return null

  return (
    <>
      {/* Backdrop overlay */}
      <div
        className="fixed inset-0 bg-black/50 z-[60]"
        onClick={onClose}
      />

      {/* Bottom sheet container */}
      <div
        className="fixed bottom-0 left-0 right-0 z-[70] animate-slide-up"
        style={{
          paddingBottom: "env(safe-area-inset-bottom)",
        }}
      >
        <div className="bg-gradient-to-b from-slate-800 to-slate-900 rounded-t-2xl border-t border-x border-cyan-500/40 shadow-lg shadow-black/50">
          {/* Drag handle */}
          <div className="flex justify-center pt-3 pb-2">
            <div className="w-12 h-1 rounded-full bg-white/30" />
          </div>

          {/* Header with unit name and close button */}
          <div className="flex items-center justify-center relative px-4 pb-3 border-b border-white/10">
            <span className="text-cyan-200 font-bold text-base">{unitName} Stats</span>
            <button
              onClick={onClose}
              className="absolute right-4 p-2 rounded-lg hover:bg-white/10 transition-colors active:bg-white/20"
            >
              <svg width="20" height="20" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M4 4L12 12M12 4L4 12" stroke="#9ca3af" strokeWidth="2" strokeLinecap="round" />
              </svg>
            </button>
          </div>

          {/* Tabs */}
          <div className="flex border-b border-white/10">
            <button
              onClick={() => setActiveTab("damage")}
              className={`flex-1 py-3 text-sm font-semibold transition-colors relative ${
                activeTab === "damage"
                  ? "text-red-400"
                  : "text-white/50 hover:text-white/70"
              }`}
            >
              <span className="flex items-center justify-center gap-2">
                <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                  <path d="M8 1L10 6H15L11 9.5L12.5 15L8 11.5L3.5 15L5 9.5L1 6H6L8 1Z" />
                </svg>
                Damage
              </span>
              {activeTab === "damage" && (
                <div className="absolute bottom-0 left-4 right-4 h-0.5 bg-red-400 rounded-full" />
              )}
            </button>
            <button
              onClick={() => setActiveTab("armor")}
              className={`flex-1 py-3 text-sm font-semibold transition-colors relative ${
                activeTab === "armor"
                  ? "text-cyan-400"
                  : "text-white/50 hover:text-white/70"
              }`}
            >
              <span className="flex items-center justify-center gap-2">
                <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                  <path d="M8 1C8 1 3 3 3 7C3 11 8 15 8 15C8 15 13 11 13 7C13 3 8 1 8 1Z" />
                </svg>
                Armor
              </span>
              {activeTab === "armor" && (
                <div className="absolute bottom-0 left-4 right-4 h-0.5 bg-cyan-400 rounded-full" />
              )}
            </button>
          </div>

          {/* Tab content */}
          <div className="p-4 min-h-[280px]">
            {activeTab === "damage" ? (
              <div className="w-full">
                <DamageTypeTooltip damageType={damageType} fullWidth />
              </div>
            ) : (
              <div className="w-full">
                <ArmorTypeTooltip armorType={armorType} armorValue={armorValue} fullWidth />
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  )
}
